import type { Appeal, AppealFile, AppealFileKey } from '@/types/appeal';

const appealFileLabels: Record<AppealFileKey, string> = {
  check: 'Чек',
  extract: 'Выписка',
  video: 'Видео',
  extra: 'Доп. файл',
};

const mimeExtensions: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'image/gif': 'GIF',
  'video/mp4': 'MP4',
  'video/webm': 'WEBM',
  'application/pdf': 'PDF',
  'application/zip': 'ZIP',
  'application/x-rar-compressed': 'RAR',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
};

const imageExtensions = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg']);
const videoExtensions = new Set(['mp4', 'webm', 'ogg', 'mov']);

export interface AppealFileEntry {
  key: AppealFileKey | 'legacy';
  label: string;
  file: AppealFile;
}

const legacyContentType = (appeal: Appeal) => {
  const extension = (appeal.file_extension || appeal.file_extansion || '')
    .replace(/^\./, '')
    .toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(extension)) {
    return `image/${extension === 'jpg' ? 'jpeg' : extension}`;
  }
  if (['mp4', 'webm'].includes(extension)) return `video/${extension}`;
  if (extension === 'pdf') return 'application/pdf';
  return 'application/octet-stream';
};

export const getAppealFiles = (appeal: Appeal): AppealFileEntry[] => {
  const entries: AppealFileEntry[] = [];
  for (const key of Object.keys(appealFileLabels) as AppealFileKey[]) {
    const file = appeal.files?.[key];
    if (file?.url) entries.push({ key, label: appealFileLabels[key], file });
  }
  if (entries.length || !appeal.file_url) return entries;
  return [{
    key: 'legacy',
    label: 'Файл',
    file: { url: appeal.file_url, type: legacyContentType(appeal) },
  }];
};

const getFileExtension = (file?: AppealFile | null) => {
  if (!file?.url) return '';
  const urlWithoutQuery = file.url.split(/[?#]/, 1)[0];
  const filename = urlWithoutQuery.split('/').pop() || '';
  return filename.match(/\.([^.]+)$/)?.[1]?.toLowerCase() || '';
};

export const getAppealFileKind = (
  file?: AppealFile | null,
  key?: AppealFileKey | 'legacy',
) => {
  const contentType = String(file?.type || '').split(';', 1)[0].trim().toLowerCase();
  const extension = getFileExtension(file) || contentType.replace(/^\./, '');
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType === 'application/pdf') return 'pdf';
  if (contentType.includes('zip') || contentType.includes('rar')) return 'archive';
  if (imageExtensions.has(extension)) return 'image';
  if (videoExtensions.has(extension)) return 'video';
  if (extension === 'pdf') return 'pdf';
  // Historical check URLs may not contain either a MIME type or an extension.
  // Let the browser try to decode them as images and fall back on img.onerror.
  if (key === 'check' || key === 'extract' || key === 'legacy') return 'image';
  return 'download';
};

export const getAppealFileExtension = (file: AppealFile) => {
  const contentType = String(file.type || '').split(';', 1)[0].toLowerCase();
  if (mimeExtensions[contentType]) return mimeExtensions[contentType];
  return getFileExtension(file).toUpperCase() || 'FILE';
};
